// CandlestickChart.tsx
import React, { useRef, useEffect } from 'react';
import * as d3 from 'd3';
import useResizeObserver from './useResizeObserver';

interface CandlestickDatum {
  date: Date;
  open: number;
  high: number;
  low: number;
  close: number;
}

interface CandlestickChartProps {
  data: {
    ticker: string;
    results: Array<{
      v: number;
      vw: number;
      o: number;
      c: number;
      h: number;
      l: number;
      t: number;
      n: number;
    }>;
  };
}

const CandlestickChart: React.FC<CandlestickChartProps> = ({ data }) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const dimensions = useResizeObserver(chartRef);

  useEffect(() => {
    if (!data?.results || data.results.length === 0 || !dimensions) return;

    // Clear previous chart
    d3.select(chartRef.current).selectAll('*').remove();

    const margin = { top: 20, right: 50, bottom: 30, left: 50 };
    const width = dimensions.width - margin.left - margin.right;
    const height = dimensions.height - margin.top - margin.bottom;

    const svg = d3
      .select(chartRef.current)
      .append('svg')
      .attr('width', dimensions.width)
      .attr('height', dimensions.height)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    const parseDate = d3.timeParse('%Q');
    const candlestickData: CandlestickDatum[] = data.results
      .map((d) => {
        const date = parseDate(d.t.toString());
        return date
          ? { date, open: d.o, high: d.h, low: d.l, close: d.c }
          : null;
      })
      .filter((d) => d) as CandlestickDatum[];

    const xScale = d3
      .scaleBand<Date>()
      .domain(candlestickData.map((d) => d.date))
      .range([0, width])
      .padding(0.3);

    const yScale = d3
      .scaleLinear()
      .domain([
        d3.min(candlestickData, (d) => d.low) ?? 0,
        d3.max(candlestickData, (d) => d.high) ?? 1,
      ])
      .range([height, 0])
      .nice();

    svg
      .append('g')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom<Date>(xScale).tickFormat(d3.timeFormat('%b %d')));

    svg.append('g').call(d3.axisLeft(yScale));

    const candles = svg
      .selectAll('g.candle')
      .data(candlestickData)
      .enter()
      .append('g')
      .attr('class', 'candle');

    // Draw stems
    candles
      .append('line')
      .attr('class', 'stem')
      .attr('x1', (d) => xScale(d.date)! + xScale.bandwidth() / 2)
      .attr('x2', (d) => xScale(d.date)! + xScale.bandwidth() / 2)
      .attr('y1', (d) => yScale(d.high))
      .attr('y2', (d) => yScale(d.low))
      .attr('stroke', 'black');

    // Draw bodies
    candles
      .append('rect')
      .attr('x', (d) => xScale(d.date)!)
      .attr('y', (d) => yScale(Math.max(d.open, d.close)))
      .attr('width', xScale.bandwidth())
      .attr('height', (d) => Math.abs(yScale(d.open) - yScale(d.close)))
      .attr('fill', (d) => (d.open > d.close ? 'red' : 'green'));
  }, [data, dimensions]);

  return <div ref={chartRef} style={{ width: '100%', height: '100%' }}></div>;
};

export default CandlestickChart;
